import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { clinicApi } from '../services/api';

interface ClinicSettings {
  id?: string;
  name?: string | null;
  logo_path?: string | null;
  domain?: string | null;
  contact_exclusions?: {
    weekdays?: string[];
    dates?: { exclusion: string; reason: string | null }[];
  };
}

export const useClinicAdmin = () => {
  const { user } = useAuth();
  const { orgId: selectedOrgId } = useOrganization();
  const [isClinicAdmin, setIsClinicAdmin] = useState(false);
  const [clinicData, setClinicData] = useState<ClinicSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkClinicAccess = useCallback(async () => {
    if (!user) {
      setIsClinicAdmin(false);
      setClinicData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await clinicApi.getSettings();
      if (response) {
        setIsClinicAdmin(true);
        setClinicData(response);
      } else {
        setIsClinicAdmin(false);
        setClinicData(null);
      }
    } catch (err: any) {
      const status = err?.errorData?.status;
      if (status && status !== 403) {
        setError(err.message || 'Failed to load clinic settings');
      }
      setIsClinicAdmin(false);
      setClinicData(null);
    } finally {
      setLoading(false);
    }
    // selectedOrgId in deps so the clinic-admin probe re-runs when the user
    // switches clinics (PHA-536).
  }, [user, selectedOrgId]);

  useEffect(() => {
    checkClinicAccess();
  }, [checkClinicAccess]);

  return {
    isClinicAdmin,
    clinicData,
    loading,
    error,
    refresh: checkClinicAccess,
  };
};

export default useClinicAdmin;
